import { QueryCache, QueryClient } from '@tanstack/react-query';
import { broadcastAuthPurge, purgeAuthState } from '@/lib/auth/authLifecycle';
import { isSessionRevokedError } from '@/lib/api/client';

/**
 * Query Client Factory
 *
 * Creates a new QueryClient instance with optimized settings for the TIL Dance platform.
 *
 * ✅ Best Practice for TanStack Start SSR:
 * - Create a NEW QueryClient for each SSR request (server-side)
 * - Reuse the SAME QueryClient on client-side (singleton pattern)
 * - This ensures proper data isolation between SSR requests
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/ssr
 * @see https://tanstack.com/start/latest/docs/framework/react/ssr
 */
export function makeQueryClient() {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        // Reactive logout (S1): a query that 401'd AND got 'unauthorized' back
        // from the refresh attempt means the session is revoked server-side.
        // Purge immediately so a tab that never navigates cannot keep
        // rendering protected routes with erroring queries. No refetch storm:
        // purgeAuthState clears the cache and re-marks the session null, which
        // flips useIsAuthenticated() so the route guard unmounts protected
        // queries; all automatic refetch triggers are off by default below.
        // Client-side only: SSR has no storage/broadcast to purge.
        if (typeof window === 'undefined') return;
        if (!isSessionRevokedError(error)) return;
        purgeAuthState(queryClient);
        broadcastAuthPurge();
      },
    }),
    defaultOptions: {
      queries: {
        // Data is considered fresh for 5 minutes
        staleTime: 1000 * 60 * 5,
        // Unused data is garbage collected after 30 minutes
        // Session-scale: with defaultStaleTime Infinity an entry never goes
        // stale, so the only way a visited page loses its cache was this GC.
        // Back-navigations must always find their data (audit round 9) —
        // payloads are small; the entry count is bounded by a session's
        // browsing.
        gcTime: Infinity,
        // Retry failed requests once
        retry: 1,
        // Don't refetch on window focus (mobile-optimized)
        refetchOnWindowFocus: false,
        // Don't refetch on reconnect (prevents duplicate requests)
        refetchOnReconnect: false,
        // Network mode for offline support
        networkMode: 'offlineFirst',
        // Disable background refetching
        refetchInterval: false,
        refetchIntervalInBackground: false,
      },
      mutations: {
        // Never auto-retry mutations: creates/deletes are not idempotent, so a
        // timed-out create would be re-sent (duplicate) and a 4xx validation
        // error would be retried pointlessly (issue #548).
        retry: false,
        // Mutations are user-intent actions: fail fast with the real error
        // instead of silently queueing offline writes that may never apply.
        networkMode: 'online',
      },
    },
  });
  return queryClient;
}

/**
 * Browser Query Client (Singleton)
 *
 * On the client-side, we create ONE QueryClient instance and reuse it.
 * This is stored in a global variable to survive HMR during development.
 */
let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  // Server-side: Always create a new QueryClient
  if (typeof window === 'undefined') {
    return makeQueryClient();
  }

  // Client-side: Reuse existing QueryClient or create new one
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
}
